import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, FileSearch, Users, ArrowRight, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const LoginPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });
  
  // Register form
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    company_id: "",
    unique_code: "",
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    // Check if already authenticated
    const checkExistingAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (response.data) {
          navigate("/dashboard", { state: { user: response.data }, replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };

    checkExistingAuth();
    fetchCompanies();
  }, [navigate]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/public/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuestLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Email dan password wajib diisi");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      const userData = response.data;
      localStorage.setItem("user", JSON.stringify(userData));
      toast.success(`Selamat datang, ${userData.name}!`);
      navigate("/dashboard", { state: { user: userData }, replace: true });
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.company_id || !registerData.unique_code || !registerData.password) {
      toast.error("Semua field wajib diisi");
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Password tidak cocok");
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: registerData.name,
        email: registerData.email,
        company_id: registerData.company_id,
        unique_code: registerData.unique_code,
        password: registerData.password
      });
      const userData = response.data;
      localStorage.setItem("user", JSON.stringify(userData));
      toast.success(`Registrasi berhasil! Selamat datang, ${userData.name}`);
      navigate("/dashboard", { state: { user: userData }, replace: true });
    } catch (error) {
      console.error("Register error:", error);
      toast.error(error.response?.data?.detail || "Registrasi gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Side - Hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1765366417060-7d87b2e854a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtb2Rlcm4lMjBibHVlfGVufDB8fHx8MTc2NTYyNjE3N3ww&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">PT. Nayaka</h1>
                <p className="text-slate-300 text-sm">Integritas Solusi Indonesia</p>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 leading-tight">
              Sistem Informasi<br />Claim Investigator
            </h2>
            <p className="text-slate-300 text-lg max-w-md">
              Platform manajemen investigasi klaim asuransi yang terintegrasi untuk memastikan proses klaim berjalan transparan dan akurat.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <FileSearch className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="font-medium">Tracking Investigasi</p>
                <p className="text-sm text-slate-400">Pantau status klaim secara real-time</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="font-medium">Multi-User Access</p>
                <p className="text-sm text-slate-400">Akses berbasis peran untuk perusahaan asuransi</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login/Register Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">PT. Nayaka Integritas Solusi Indonesia</h1>
            <p className="text-slate-600 mt-1">Claim Investigator System</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="tab-login">Masuk</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Daftar</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Selamat Datang</h2>
                  <p className="text-slate-600">Silakan login untuk mengakses dashboard</p>
                </div>

                {/* Google Login for Admin/SuperUser */}
                <Button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg flex items-center justify-center gap-3 transition-all mb-4"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Login dengan Google
                  <ArrowRight className="w-4 h-4" />
                </Button>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-slate-500">atau login sebagai Guest</span>
                  </div>
                </div>

                {/* Guest Login Form */}
                <form onSubmit={handleGuestLogin} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email</Label>
                    <Input
                      data-testid="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      placeholder="email@perusahaan.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative mt-1">
                      <Input
                        data-testid="login-password"
                        type={showPassword ? "text" : "password"}
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        placeholder="Masukkan password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit"
                    disabled={loading}
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  >
                    {loading ? "Memproses..." : "Masuk"}
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-slate-500">
                  Belum punya akun?{" "}
                  <button
                    onClick={() => setActiveTab("register")}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Daftar sekarang
                  </button>
                </p>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Daftar Akun Guest</h2>
                  <p className="text-slate-600">Untuk perusahaan asuransi partner</p>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Nama Lengkap *</Label>
                    <Input
                      data-testid="register-name"
                      type="text"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      placeholder="Masukkan nama lengkap"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Email *</Label>
                    <Input
                      data-testid="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      placeholder="email@perusahaan.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Perusahaan Asuransi *</Label>
                    <Select 
                      value={registerData.company_id} 
                      onValueChange={(value) => setRegisterData({ ...registerData, company_id: value })}
                    >
                      <SelectTrigger className="mt-1" data-testid="register-company">
                        <SelectValue placeholder="Pilih perusahaan asuransi" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.length > 0 ? (
                          companies.map((company) => (
                            <SelectItem key={company.company_id} value={company.company_id}>
                              {company.name}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>
                            Belum ada perusahaan terdaftar
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Kode Unik Perusahaan *</Label>
                    <Input
                      data-testid="register-unique-code"
                      type="text"
                      value={registerData.unique_code}
                      onChange={(e) => setRegisterData({ ...registerData, unique_code: e.target.value })}
                      placeholder="Masukkan kode unik dari perusahaan"
                      className="mt-1 font-mono"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Dapatkan kode ini dari perusahaan asuransi Anda
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Password *</Label>
                    <div className="relative mt-1">
                      <Input
                        data-testid="register-password"
                        type={showPassword ? "text" : "password"}
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        placeholder="Minimal 6 karakter"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Konfirmasi Password *</Label>
                    <Input
                      data-testid="register-confirm-password"
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      placeholder="Ulangi password"
                      className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="register-submit"
                    disabled={loading || companies.length === 0}
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                  >
                    {loading ? "Memproses..." : "Daftar"}
                  </Button>
                </form>

                <p className="mt-4 text-center text-sm text-slate-500">
                  Sudah punya akun?{" "}
                  <button
                    onClick={() => setActiveTab("login")}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Masuk
                  </button>
                </p>
              </TabsContent>
            </Tabs>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-slate-500">
              &copy; 2025 PT. Nayaka Integritas Solusi Indonesia. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
